import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Users } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import PermissionMatrixEditor from './PermissionMatrixEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Centralized per-user module permission manager (every platform module/table).
export const ModuleAccessManager = () => {
  const [users, setUsers] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [matrix, setMatrix] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/users`).then((r) => setUsers(r.data || [])).catch(() => {});
  }, []);

  const selected = users.find((u) => u.id === selectedId);
  useEffect(() => {
    if (selected) setMatrix(selected.permissions_v2 || {});
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/users/${selectedId}`, { permissions_v2: matrix });
      toast.success('Module permissions updated — takes effect on the user\'s next page load');
      const r = await axios.get(`${API_URL}/api/admin/users`);
      setUsers(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="module-access-manager">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-base flex items-center gap-2"><Users className="h-4 w-4" /> Module Access — per user</CardTitle>
        <CardDescription>
          Every management module/table on the platform with its View / Create / Edit / Delete / Export / Approve permissions.
          Pick a user, adjust their matrix, and save. Navigation and API access follow these permissions.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="max-w-md">
          <Select value={selectedId} onValueChange={setSelectedId}>
            <SelectTrigger data-testid="module-access-user-select"><SelectValue placeholder="Select a user…" /></SelectTrigger>
            <SelectContent>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.full_name} — {u.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {selected && (
          <>
            <PermissionMatrixEditor role={selected.role} value={matrix} onChange={setMatrix} />
            <div className="flex justify-end">
              <Button onClick={save} disabled={saving} data-testid="module-access-save-btn">
                {saving ? 'Saving…' : 'Save Module Permissions'}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ModuleAccessManager;
